import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { Loader2 } from "lucide-react";
import { getInvoice } from "@/lib/accounting/accounting.functions";
import { fmtDate, fmtMoney } from "@/lib/accounting/exports";

export const Route = createFileRoute("/_authenticated/admin/accounting/invoices/$id/print")({
  component: PrintInvoice,
});

function PrintInvoice() {
  const { id } = Route.useParams();
  const fn = useServerFn(getInvoice);
  const { data, isLoading } = useQuery({ queryKey: ["accounting", "invoice", id, "print"], queryFn: () => fn({ data: { id } }) });

  useEffect(() => {
    if (data) setTimeout(() => window.print(), 250);
  }, [data]);

  if (isLoading || !data) return <div className="py-20 grid place-items-center bg-white"><Loader2 className="size-6 animate-spin" /></div>;
  const { invoice, line_items } = data;

  return (
    <div className="bg-white text-zinc-900 min-h-screen p-10 print:p-0 max-w-3xl mx-auto" style={{ fontFamily: "Georgia, serif" }}>
      <style>{`@media print { @page { margin: 0.5in; } body { background: white !important; } .no-print { display: none !important; } }`}</style>
      <header className="flex justify-between items-start border-b-2 border-amber-700 pb-6 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-zinc-900">Michael's Painting</h1>
          <p className="text-sm text-zinc-600 mt-1">Madison Metro Area, Wisconsin</p>
          <p className="text-sm text-zinc-600">(608) 886-4598 · michaelspainting0@gmail.com</p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-widest text-amber-700 font-semibold">Invoice</p>
          <p className="text-xl font-mono mt-1">{invoice.invoice_number}</p>
        </div>
      </header>

      <section className="grid grid-cols-2 gap-6 mb-6 text-sm">
        <div>
          <p className="text-xs uppercase text-zinc-500 font-semibold mb-1">Bill to</p>
          <p className="font-semibold">{invoice.customer_name}</p>
          {invoice.customer_email && <p>{invoice.customer_email}</p>}
          {invoice.property_address && <p className="text-zinc-600">{invoice.property_address}</p>}
        </div>
        <div className="text-right">
          <p className="text-xs uppercase text-zinc-500 font-semibold mb-1">Details</p>
          <p><span className="text-zinc-500">Issued:</span> {fmtDate(invoice.issue_date)}</p>
          <p><span className="text-zinc-500">Due:</span> {fmtDate(invoice.due_date)}</p>
          <p className="capitalize"><span className="text-zinc-500">Status:</span> {invoice.status.replace("_", " ")}</p>
        </div>
      </section>

      <table className="w-full text-sm mb-6">
        <thead>
          <tr className="border-b-2 border-zinc-900">
            <th className="text-left py-2">Description</th>
            <th className="text-right py-2 w-20">Qty</th>
            <th className="text-right py-2 w-28">Rate</th>
            <th className="text-right py-2 w-28">Amount</th>
          </tr>
        </thead>
        <tbody>
          {line_items.map((l: any) => (
            <tr key={l.id} className="border-b border-zinc-200">
              <td className="py-2.5">
                <p className="font-medium">{l.description}</p>
                <p className="text-xs text-zinc-500 capitalize">{l.kind.replace("_", " ")}</p>
              </td>
              <td className="text-right">{Number(l.quantity)}</td>
              <td className="text-right">{fmtMoney(l.unit_price)}</td>
              <td className="text-right font-medium">{fmtMoney(l.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="ml-auto max-w-xs text-sm space-y-1">
        <Row k="Subtotal" v={fmtMoney(invoice.subtotal)} />
        <Row k={`Tax (${(Number(invoice.tax_rate) * 100).toFixed(2)}%)`} v={fmtMoney(invoice.tax_amount)} />
        <Row k="Total" v={fmtMoney(invoice.total)} bold />
        <Row k="Paid to date" v={fmtMoney(invoice.amount_paid)} />
        <div className="border-t-2 border-amber-700 pt-2 mt-2">
          <Row k="Balance due" v={fmtMoney(invoice.balance_due)} bold />
        </div>
      </div>

      {invoice.notes && (<section className="mt-8"><p className="text-xs uppercase text-zinc-500 font-semibold mb-1">Notes</p><p className="text-sm whitespace-pre-wrap">{invoice.notes}</p></section>)}
      {invoice.terms && (<section className="mt-6"><p className="text-xs uppercase text-zinc-500 font-semibold mb-1">Terms</p><p className="text-xs text-zinc-600 whitespace-pre-wrap">{invoice.terms}</p></section>)}

      <footer className="mt-10 pt-4 border-t border-zinc-200 text-center text-xs text-zinc-500">
        Thank you for your business. — Michael's Painting
      </footer>
    </div>
  );
}

function Row({ k, v, bold }: { k: string; v: string; bold?: boolean }) {
  return <div className={`flex justify-between ${bold ? "font-bold text-zinc-900" : "text-zinc-600"}`}><span>{k}</span><span>{v}</span></div>;
}